import type {
  SankeyLayoutLink,
  SankeyLayoutModel,
  SankeyLayoutNode,
} from '../types/sankey-internal.js';
import { isResolvedNode } from './is-resolved-sankey-node.js';

/**
 * Id lookups for a single layout. `linksBySource` maps `sourceId` → `targetId`
 * → the links between that pair. That array holds a single link in the normal
 * case; it holds more only for parallel links (multiple links between the same
 * node pair), which are rare. Resolving one of those by its `linkIndex` with a
 * short `find` reads more simply than a third nested map and, given the array
 * is almost always length one, is effectively just as fast.
 */
export interface SankeyLayoutIndex {
  nodesById: Map<string, SankeyLayoutNode>;
  linksBySource: Map<string, Map<string, SankeyLayoutLink[]>>;
}

/**
 * Per-layout indexes cached against the layout object itself. A layout rebuilt
 * on resize or data change is a new object, so it misses the cache and gets a
 * fresh index; the previous layout and its index become unreachable together
 * and are garbage-collected. The `WeakMap` is what makes that automatic — a
 * lookup can never hand back a node or link from a stale layout, and no manual
 * cache invalidation is needed.
 */
const indexCache = new WeakMap<SankeyLayoutModel, SankeyLayoutIndex>();

function addLink(
  linksBySource: Map<string, Map<string, SankeyLayoutLink[]>>,
  sourceId: string,
  targetId: string,
  link: SankeyLayoutLink,
): void {
  let byTarget = linksBySource.get(sourceId);
  if (!byTarget) {
    byTarget = new Map<string, SankeyLayoutLink[]>();
    linksBySource.set(sourceId, byTarget);
  }
  const links = byTarget.get(targetId);
  if (links) {
    links.push(link);
  } else {
    byTarget.set(targetId, [link]);
  }
}

/**
 * Fallback index build, used for layouts not produced by
 * `generateSankeyLayout` (e.g. hand-built test fixtures) and the empty-graph
 * case. The primary path reuses d3-sankey's own pass — see `primeLayoutIndex`.
 */
function buildIndex(layout: SankeyLayoutModel): SankeyLayoutIndex {
  const nodesById = new Map<string, SankeyLayoutNode>();
  for (const node of layout.nodes) {
    nodesById.set(node.id, node);
  }

  const linksBySource = new Map<string, Map<string, SankeyLayoutLink[]>>();
  for (const link of layout.links) {
    const sourceId = String(
      isResolvedNode(link.source) ? link.source.id : link.source,
    );
    const targetId = String(
      isResolvedNode(link.target) ? link.target.id : link.target,
    );
    addLink(linksBySource, sourceId, targetId, link);
  }

  return { nodesById, linksBySource };
}

/**
 * Seeds the cache with an index that `generateSankeyLayout` collected during
 * d3-sankey's own single pass over the graph, so the resolvers stay `O(1)`
 * without `buildIndex` ever re-scanning the nodes and links.
 */
export function primeLayoutIndex(
  layout: SankeyLayoutModel,
  index: SankeyLayoutIndex,
): void {
  indexCache.set(layout, index);
}

function getLayoutIndex(layout: SankeyLayoutModel): SankeyLayoutIndex {
  const cached = indexCache.get(layout);
  if (cached) {
    return cached;
  }
  const index = buildIndex(layout);
  indexCache.set(layout, index);
  return index;
}

/**
 * Resolves a node by id against the current layout in `O(1)`, or `undefined`
 * when the id is absent (e.g. after a data change dropped it).
 */
export function resolveLayoutNode(
  layout: SankeyLayoutModel,
  nodeId: string,
): SankeyLayoutNode | undefined {
  return getLayoutIndex(layout).nodesById.get(nodeId);
}

/**
 * Resolves a link by its source/target ids and per-layout link index against
 * the current layout, together with both resolved endpoint nodes. Returns
 * `undefined` when the link is absent or either endpoint is unresolved. The
 * `linkIndex` discriminant picks the right link when parallel links share the
 * same source and target ids; with the usual single link per pair it simply
 * matches that one.
 */
export function resolveLayoutLink(
  layout: SankeyLayoutModel,
  sourceId: string,
  targetId: string,
  linkIndex: number,
):
  | {
      link: SankeyLayoutLink;
      source: SankeyLayoutNode;
      target: SankeyLayoutNode;
    }
  | undefined {
  const links = getLayoutIndex(layout)
    .linksBySource.get(sourceId)
    ?.get(targetId);
  const link = links?.find(
    (candidate) => (candidate.__index ?? 0) === linkIndex,
  );
  if (!link) {
    return undefined;
  }

  const source = isResolvedNode(link.source) ? link.source : undefined;
  const target = isResolvedNode(link.target) ? link.target : undefined;
  if (!source || !target) {
    return undefined;
  }

  return { link, source, target };
}
